import { useState, useEffect } from 'react';

/**
 * Hook para verificar permisos del usuario actual
 * @returns {Object} Objeto con funciones de verificación de permisos
 */
export const usePermissions = () => {
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    // Obtener permisos del localStorage (establecidos al hacer login)
    const user = localStorage.getItem('currentUser');
    if (user) {
      try {
        const userData = JSON.parse(user);
        const permissions = userData.permissions || [];
        const role = userData.role || 'guest';

        setUserPermissions(Array.isArray(permissions) ? permissions : JSON.parse(permissions || '[]'));
        setUserRole(role);
      } catch (error) {
        console.error('Error parsing user permissions:', error);
        setUserPermissions([]);
        setUserRole('guest');
      }
    }
  }, []);

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param {string} permission - Permiso a verificar ('read', 'write', 'delete', 'admin')
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    // Admin siempre tiene todos los permisos
    if (userRole === 'admin' || userPermissions.includes('admin')) {
      return true;
    }

    return userPermissions.includes(permission);
  };

  /**
   * Verifica si el usuario puede leer/ver
   * @returns {boolean}
   */
  const canRead = () => {
    return hasPermission('read') || hasPermission('write') || hasPermission('admin');
  };

  /**
   * Verifica si el usuario puede escribir/editar
   * @returns {boolean}
   */
  const canWrite = () => {
    return hasPermission('write') || hasPermission('admin');
  };

  /**
   * Verifica si el usuario puede eliminar
   * @returns {boolean}
   */
  const canDelete = () => {
    return hasPermission('delete') || hasPermission('admin');
  };

  /**
   * Verifica si el usuario es administrador
   * @returns {boolean}
   */
  const isAdmin = () => {
    return userRole === 'admin' || hasPermission('admin');
  };

  /**
   * Verifica si el usuario tiene solo permisos de lectura
   * @returns {boolean}
   */
  const isReadOnly = () => {
    return !canWrite() && !canDelete() && !isAdmin();
  };

  return {
    userPermissions,
    userRole,
    hasPermission,
    canRead,
    canWrite,
    canDelete,
    isAdmin,
    isReadOnly,
  };
};

export default usePermissions;
